import React from 'react';
import { useTranslation } from 'react-i18next';
import { UploadStatus } from './MenuUploadStatus';

interface InlineMenuUploadStatusProps {
  status: UploadStatus;
  progress?: number;
  errorMessage?: string;
  onRetry?: () => void;
  onContinue?: () => void;
}

const InlineMenuUploadStatus: React.FC<InlineMenuUploadStatusProps> = ({
  status,
  progress = 0,
  errorMessage,
  onRetry,
  onContinue,
}) => {
  const { t } = useTranslation(['catalogue']);

  const statusMessages = {
    uploading: t('upload_status.uploading', 'Uploading your menu...'),
    processing: t('upload_status.processing', 'Processing your files...'),
    analyzing: t('upload_status.analyzing', 'Analyzing menu content...'),
    generating: t(
      'upload_status.generating',
      'Generating your catalog items...'
    ),
    completed: t(
      'upload_status.completed',
      'Your menu has been created successfully!'
    ),
    failed: t('upload_status.failed', 'Failed to process your menu'),
  };

  const isLoading = [
    'uploading',
    'processing',
    'analyzing',
    'generating',
  ].includes(status);

  const getBgColor = () => {
    switch (status) {
      case 'completed':
        return 'bg-green-50 border-green-200';
      case 'failed':
        return 'bg-red-50 border-red-200';
      default:
        return 'bg-blue-50 border-blue-200';
    }
  };

  const getIconColor = () => {
    switch (status) {
      case 'completed':
        return 'text-green-500';
      case 'failed':
        return 'text-red-500';
      default:
        return 'text-blue-500';
    }
  };

  return (
    <div className={`w-full border rounded-lg px-6 py-5 mb-8 ${getBgColor()}`}>
      <div className='flex flex-col md:flex-row items-center'>
        {/* Status Icon */}
        <div className='flex-shrink-0 mr-4'>
          {status === 'completed' ? (
            <div className='rounded-full bg-green-100 p-3 flex items-center justify-center'>
              <svg
                xmlns='http://www.w3.org/2000/svg'
                className='h-6 w-6 text-green-500'
                fill='none'
                viewBox='0 0 24 24'
                stroke='currentColor'
              >
                <path
                  strokeLinecap='round'
                  strokeLinejoin='round'
                  strokeWidth={2}
                  d='M5 13l4 4L19 7'
                />
              </svg>
            </div>
          ) : status === 'failed' ? (
            <div className='rounded-full bg-red-100 p-3 flex items-center justify-center'>
              <svg
                xmlns='http://www.w3.org/2000/svg'
                className='h-6 w-6 text-red-500'
                fill='none'
                viewBox='0 0 24 24'
                stroke='currentColor'
              >
                <path
                  strokeLinecap='round'
                  strokeLinejoin='round'
                  strokeWidth={2}
                  d='M6 18L18 6M6 6l12 12'
                />
              </svg>
            </div>
          ) : (
            <div className='rounded-full bg-blue-100 p-3 flex items-center justify-center'>
              <svg
                className='animate-spin h-6 w-6 text-blue-500'
                xmlns='http://www.w3.org/2000/svg'
                fill='none'
                viewBox='0 0 24 24'
              >
                <circle
                  className='opacity-25'
                  cx='12'
                  cy='12'
                  r='10'
                  stroke='currentColor'
                  strokeWidth='4'
                ></circle>
                <path
                  className='opacity-75'
                  fill='currentColor'
                  d='M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z'
                ></path>
              </svg>
            </div>
          )}
        </div>

        {/* Status Content */}
        <div className='flex-grow text-center md:text-left mt-4 md:mt-0'>
          <h3 className={`text-lg font-semibold ${getIconColor()}`}>
            {statusMessages[status]}
          </h3>

          {isLoading && (
            <>
              <p className='text-sm text-gray-600 mb-2'>
                {t(
                  'upload_status.patience',
                  'This might take a few minutes. Please be patient.'
                )}
              </p>
              <div className='w-full bg-gray-200 rounded-full h-2.5 mt-2'>
                <div
                  className='bg-droov h-2.5 rounded-full transition-all duration-300'
                  style={{ width: `${progress}%` }}
                ></div>
              </div>
            </>
          )}

          {status === 'failed' && errorMessage && (
            <p className='text-sm text-red-600 mt-1'>{errorMessage}</p>
          )}

          {status === 'completed' && (
            <p className='text-sm text-green-600 mt-1'>
              {t(
                'upload_status.completed_message',
                'Your catalog has been populated with items from your menu.'
              )}
            </p>
          )}
        </div>

        {/* Action Buttons */}
        <div className='ml-0 md:ml-4 mt-4 md:mt-0'>
          {status === 'failed' && onRetry && (
            <button
              onClick={onRetry}
              className='px-4 py-2 bg-droov text-white rounded-md hover:bg-droov/90'
            >
              {t('try_again.button', 'Try Again')}
            </button>
          )}

          {status === 'completed' && onContinue && (
            <button
              onClick={onContinue}
              className='px-4 py-2 bg-droov text-white rounded-md hover:bg-droov/90'
            >
              {t('view_catalog.button', 'View My Catalog')}
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default InlineMenuUploadStatus;
